import React, { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, ChevronLeft, ChevronRight, X, Lock } from "lucide-react";
import fr from "@/content/fr";
import { trackViewContent } from "@/lib/analytics";

/**
 * Horizontal, swipeable slider of portrait teaser cards (9:16 — Reels/TikTok feel).
 *
 * To switch to square (1:1) cards, change `CARD_RATIO` below to "aspect-square"
 * and adjust `CARD_W` if needed.
 */
const CARD_RATIO = "aspect-[9/16]";
const CARD_W = "w-[72vw] sm:w-[300px] lg:w-[320px]";

export const EditionsSection = () => {
  const scrollerRef = useRef(null);
  const [openCard, setOpenCard] = useState(null);

  const scrollByCard = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const child = el.querySelector("[data-card]");
    const step = child ? child.getBoundingClientRect().width + 20 : 320;
    el.scrollBy({ left: step * dir, behavior: "smooth" });
  };

  const onPlay = (card) => {
    trackViewContent({ content_name: `edition_teaser_${card.id}` });
    setOpenCard(card);
  };

  // Keyboard arrows on the slider region
  const onKey = (e) => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      scrollByCard(1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      scrollByCard(-1);
    }
  };

  useEffect(() => {
    if (!openCard) return;
    const onEsc = (e) => e.key === "Escape" && setOpenCard(null);
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [openCard]);

  return (
    <section
      id="editions"
      data-testid="editions-section"
      className="relative py-24 sm:py-32 lg:py-40 overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background:
            "linear-gradient(180deg, #15042E 0%, #1B0840 60%, #15042E 100%)",
        }}
      />
      <div
        aria-hidden
        className="absolute -bottom-32 left-[-10%] w-[600px] h-[600px] rounded-full blur-3xl opacity-25"
        style={{ background: "radial-gradient(closest-side, rgba(186,120,255,0.6), transparent)" }}
      />

      <div className="relative max-w-[1320px] mx-auto px-5 sm:px-8 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 lg:gap-10">
          <div className="max-w-[720px]">
            <motion.p
              initial={{ y: 16, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.6 }}
              className="text-[11px] tracking-[0.34em] uppercase text-white/55 font-mono"
            >
              Programme annuel
            </motion.p>
            <motion.h2
              initial={{ y: 22, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="mt-4 font-display font-extrabold text-white uppercase leading-[0.96] tracking-[-0.01em]"
              style={{ fontSize: "clamp(2rem, 5vw, 3.5rem)" }}
              data-testid="editions-title"
            >
              {fr.editions.title}
            </motion.h2>
            <motion.p
              initial={{ y: 16, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="mt-5 max-w-[560px] text-[15.5px] sm:text-[16.5px] leading-[1.7] text-white/65"
            >
              {fr.editions.subtitle}
            </motion.p>
          </div>

          {/* Desktop arrows */}
          <div className="hidden md:flex items-center gap-3">
            <button
              type="button"
              onClick={() => scrollByCard(-1)}
              aria-label={fr.editions.prev}
              data-testid="editions-prev"
              className="w-12 h-12 rounded-full border border-white/15 text-white/80 hover:text-white hover:border-white/40 hover:bg-white/[0.04] transition flex items-center justify-center"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              type="button"
              onClick={() => scrollByCard(1)}
              aria-label={fr.editions.next}
              data-testid="editions-next"
              className="w-12 h-12 rounded-full border border-white/15 text-white/80 hover:text-white hover:border-white/40 hover:bg-white/[0.04] transition flex items-center justify-center"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        {/* Slider */}
        <div
          ref={scrollerRef}
          onKeyDown={onKey}
          tabIndex={0}
          aria-label="Carrousel des éditions 2027"
          data-testid="editions-slider"
          className="mt-12 flex gap-5 overflow-x-auto overflow-y-hidden snap-x snap-mandatory pb-6 -mx-5 sm:-mx-8 lg:-mx-12 px-5 sm:px-8 lg:px-12 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 rounded-3xl"
          style={{ scrollbarWidth: "none", overscrollBehaviorX: "contain" }}
        >
          {fr.editions.cards.map((card, i) => (
            <motion.button
              key={card.id}
              data-card
              data-testid={`edition-card-${card.id}`}
              onClick={() => onPlay(card)}
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: 0.05 * i }}
              className={`group relative shrink-0 ${CARD_W} ${CARD_RATIO} snap-start rounded-3xl overflow-hidden text-left bg-black border border-white/10 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E91E63]`}
            >
              {/* black card surface w/ subtle purple glow */}
              <div className="absolute inset-0 bg-black" />
              <img
                src="/images/mystery-video.png"
                alt=""
                aria-hidden
                className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-60 transition-opacity duration-500"
                loading="lazy"
              />
              <div
                aria-hidden
                className="absolute inset-0 opacity-50 group-hover:opacity-80 transition-opacity duration-500"
                style={{
                  background:
                    "radial-gradient(60% 50% at 50% 60%, rgba(186,120,255,0.35), transparent 70%)",
                }}
              />
              {/* grain */}
              <div
                aria-hidden
                className="absolute inset-0 opacity-[0.10] mix-blend-overlay"
                style={{
                  backgroundImage:
                    "radial-gradient(rgba(255,255,255,0.6) 0.6px, transparent 0.6px)",
                  backgroundSize: "3px 3px",
                }}
              />
              {/* faint diamond watermark */}
              <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full opacity-[0.08]" aria-hidden>
                <g fill="none" stroke="#FAFAFA" strokeWidth="0.5" transform="translate(100 100)">
                  <polygon points="0,-60 50,-15 0,70 -50,-15" />
                  <line x1="-50" y1="-15" x2="50" y2="-15" />
                </g>
              </svg>

              {/* Top label */}
              <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
                <span className="text-[10px] tracking-[0.32em] uppercase text-white/60 font-mono">
                  {fr.editions.cardLabel}
                </span>
                <Lock size={12} className="text-white/45" aria-hidden />
              </div>

              {/* Play button */}
              <div className="absolute inset-0 flex items-center justify-center">
                <span
                  className="relative inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/[0.05] border border-white/30 backdrop-blur-md group-hover:scale-110 transition-transform duration-300"
                  aria-hidden
                >
                  <span
                    className="absolute inset-0 rounded-full opacity-60 group-hover:opacity-100 transition-opacity"
                    style={{ boxShadow: "0 0 38px 6px rgba(186,120,255,0.55)" }}
                  />
                  <Play size={26} className="text-white relative translate-x-[2px]" fill="currentColor" />
                </span>
              </div>

              {/* Bottom meta */}
              <div className="absolute bottom-5 left-5 right-5">
                <p className="font-display font-extrabold text-white text-xl uppercase tracking-tight leading-none">
                  {card.code}
                </p>
                <p className="mt-2 text-[12px] tracking-[0.18em] uppercase text-white/55 font-mono">
                  {card.hint}
                </p>
              </div>
            </motion.button>
          ))}
          {/* trailing spacer to enable a slight peek at the end */}
          <div className="shrink-0 w-2" aria-hidden />
        </div>

        {/* Mobile arrows */}
        <div className="md:hidden flex items-center justify-center gap-4 mt-2">
          <button
            type="button"
            onClick={() => scrollByCard(-1)}
            aria-label={fr.editions.prev}
            className="w-11 h-11 rounded-full border border-white/15 text-white/80 hover:text-white hover:border-white/40 transition flex items-center justify-center"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            onClick={() => scrollByCard(1)}
            aria-label={fr.editions.next}
            className="w-11 h-11 rounded-full border border-white/15 text-white/80 hover:text-white hover:border-white/40 transition flex items-center justify-center"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      {/* "Bientôt dévoilé" modal */}
      <AnimatePresence>
        {openCard && (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center p-5"
            role="dialog"
            aria-modal="true"
            aria-labelledby="edition-modal-title"
            data-testid="edition-modal"
          >
            <motion.div
              className="absolute inset-0 bg-[#0A0118]/85 backdrop-blur-md"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpenCard(null)}
            />
            <motion.div
              initial={{ y: 24, opacity: 0, scale: 0.95 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 24, opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.35 }}
              className="relative max-w-md w-full rounded-3xl p-7 sm:p-8 bg-gradient-to-br from-[#2A0A52] to-[#0A0118] border border-white/10 shadow-2xl text-center"
            >
              <button
                type="button"
                onClick={() => setOpenCard(null)}
                aria-label="Fermer"
                className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center text-white/65 hover:text-white hover:bg-white/10 transition"
                data-testid="edition-modal-close"
              >
                <X size={18} />
              </button>
              <p className="text-[10px] tracking-[0.32em] uppercase text-white/55 font-mono">
                {openCard.code}
              </p>
              <h3
                id="edition-modal-title"
                className="mt-3 font-display font-extrabold text-white text-3xl uppercase tracking-tight"
              >
                {fr.editions.cardLockedTitle}
              </h3>
              <p className="mt-4 text-white/70 text-[15px] leading-[1.65]">
                {fr.editions.cardLockedBody}
              </p>
              <div className="mt-6 inline-flex items-center gap-2 text-[11px] tracking-[0.32em] uppercase text-[#E91E63] font-mono">
                <Lock size={12} /> {openCard.hint}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default EditionsSection;
